import React from 'react';
import { Box, Typography, Button, Container, Grid, Paper } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SearchIcon from '@mui/icons-material/Search';
import InsightsIcon from '@mui/icons-material/Insights';
import HubIcon from '@mui/icons-material/Hub';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import GavelIcon from '@mui/icons-material/Gavel';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import GppBadIcon from '@mui/icons-material/GppBad';
import EditNoteIcon from '@mui/icons-material/EditNote';
import BarChartIcon from '@mui/icons-material/BarChart';
import { getConfig } from '../../common/env-config';

// Placeholder for the generated hero image - normally this would be imported
// const heroImage = require('../../../assets/discover_creators_hero_collage.png'); 

const DiscoverCreators = () => {
    const { ecWebsiteUrl } = getConfig();
    return (
        <Box className="bg-white min-h-screen font-sans text-gray-900 pt-20">
            {/* 1. Hero Section with Collage Visual */}
            <Container maxWidth="lg" sx={{ pt: 6, pb: 10 }}>
                <Grid container spacing={6} alignItems="center">
                    <Grid item xs={12} md={6}>
                        <div className="inline-block px-4 py-1.5 rounded-full bg-blue-50 text-blue-600 font-bold text-xs uppercase tracking-wide mb-6 animate-fade-in-up">
                            Platform Overview
                        </div>
                        <Typography variant="h1" className="text-4xl lg:text-5xl font-extrabold text-gray-900 leading-tight mb-6 animate-fade-in-up delay-100">
                            The End-to-End <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">Influencer Relationship</span> & Campaign Management Platform
                        </Typography>
                        <Typography variant="body1" className="text-lg text-gray-600 mb-8 leading-relaxed animate-fade-in-up delay-200">
                            Oye Creators is a data-driven influencer marketing platform that enables brands and agencies to discover, manage, collaborate with, and measure creators at scale — with a strong focus on nano, mini-micro, and hyperlocal creators across India.
                        </Typography>
                        <div className="flex flex-wrap gap-4 animate-fade-in-up delay-300">
                            <Button
                                variant="contained"
                                size="large"
                                href="https://outlook.office365.com/book/ProductDemo@ttlmedia.in/?ismsaljsauthenabled=true"
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                    borderRadius: '12px',
                                    textTransform: 'none',
                                    fontSize: '1rem',
                                    fontWeight: 'bold',
                                    background: 'linear-gradient(to right, #2563eb, #7c3aed)',
                                    boxShadow: '0 10px 25px -5px rgba(37, 99, 235, 0.4)',
                                    px: 4, py: 1.5,
                                    transition: 'transform 0.2s',
                                    '&:hover': { transform: 'scale(1.05)', boxShadow: '0 15px 30px -5px rgba(37, 99, 235, 0.5)' }
                                }}
                            >
                                Book a Demo
                            </Button>
                            <Button
                                variant="outlined"
                                size="large"
                                href={ecWebsiteUrl + "signup"}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                    borderRadius: '12px',
                                    textTransform: 'none',
                                    fontSize: '1rem',
                                    fontWeight: 'bold',
                                    borderColor: '#e5e7eb',
                                    color: '#374151',
                                    px: 4, py: 1.5,
                                    transition: 'all 0.2s',
                                    '&:hover': { borderColor: '#9ca3af', backgroundColor: '#f9fafb', transform: 'scale(1.05)' }
                                }}
                            >
                                Start Free Trial
                            </Button>
                        </div>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        {/* Abstract Collage Visual representation - Updated for visual richness */}
                        <Box className="relative w-full aspect-square md:aspect-[4/3] animate-float">
                            <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 to-blue-50 rounded-3xl border border-white shadow-2xl overflow-hidden flex items-center justify-center">
                                {/* Simulated 3D Glass Network Graph */}
                                <div className="absolute inset-0 opacity-60">
                                    <div className="absolute top-[20%] left-[20%] w-32 h-32 bg-purple-400/30 rounded-full blur-3xl animate-pulse-slow"></div>
                                    <div className="absolute bottom-[20%] right-[20%] w-40 h-40 bg-blue-400/30 rounded-full blur-3xl animate-pulse-slow delay-1000"></div>
                                </div>

                                {/* Center Hub */}
                                <div className="relative z-10 bg-white/40 backdrop-blur-md border border-white/60 p-6 rounded-2xl shadow-xl">
                                    <HubIcon sx={{ fontSize: 48, color: '#4f46e5' }} />
                                    <div className="mt-2 text-xs font-bold text-gray-700 text-center">Creator<br />Network</div>
                                </div>

                                {/* Satellites */}
                                <div className="absolute top-[15%] right-[15%] bg-white/60 backdrop-blur-sm p-3 rounded-xl shadow-lg animate-bounce-custom delay-100">
                                    <InsightsIcon sx={{ color: '#ec4899' }} />
                                </div>
                                <div className="absolute bottom-[20%] left-[10%] bg-white/60 backdrop-blur-sm p-3 rounded-xl shadow-lg animate-bounce-custom delay-300">
                                    <SearchIcon sx={{ color: '#06b6d4' }} />
                                </div>
                                <div className="absolute top-[40%] left-[5%] bg-white/60 backdrop-blur-sm p-3 rounded-xl shadow-lg animate-bounce-custom delay-500">
                                    <VerifiedUserIcon sx={{ color: '#10b981' }} />
                                </div>
                                {/* Connecting Lines (Simulated with SVG) */}
                                <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-30">
                                    <path d="M200 200 L300 100" stroke="#6366f1" strokeWidth="2" strokeDasharray="5,5" />
                                    <path d="M200 200 L100 300" stroke="#6366f1" strokeWidth="2" strokeDasharray="5,5" />
                                    <path d="M200 200 L100 150" stroke="#6366f1" strokeWidth="2" strokeDasharray="5,5" />
                                </svg>
                            </div>
                        </Box>
                    </Grid>
                </Grid>
            </Container>

            {/* 2. Challenge/Solution Section - UPDATED: No Emojis, Tighter Spacing, Icons */}
            <Box className="bg-gray-50 py-16 px-6">
                <Container maxWidth="lg">
                    <div className="text-center max-w-3xl mx-auto mb-12">
                        <Typography variant="h6" className="font-bold text-gray-400 uppercase tracking-widest text-sm mb-3">Built for Modern Marketing</Typography>
                        <Typography variant="h2" className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                            Solving Real Operational Challenges
                        </Typography>
                        <Typography className="text-gray-600 text-lg">
                            Influencer marketing today demands scale, authenticity, and speed. We solve the fragmentation that slows you down.
                        </Typography>
                    </div>

                    <Grid container spacing={3}>
                        {[
                            { title: 'Fragmented Communication', desc: 'No more scattered DMs and emails.', icon: <ChatBubbleOutlineIcon fontSize="large" className="text-blue-500" /> },
                            { title: 'Unreliable Quality', desc: 'Eliminate fake followers & engagement.', icon: <GppBadIcon fontSize="large" className="text-red-500" /> },
                            { title: 'Manual Tracking', desc: 'Stop updating spreadsheets manually.', icon: <EditNoteIcon fontSize="large" className="text-orange-500" /> },
                            { title: 'ROI Clarity', desc: 'Get clear visibility on performance.', icon: <BarChartIcon fontSize="large" className="text-purple-500" /> }
                        ].map((item, idx) => (
                            <Grid item xs={12} sm={6} md={3} key={idx}>
                                <Paper
                                    elevation={0}
                                    className="p-6 h-full rounded-2xl border border-gray-100 bg-white hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group cursor-default"
                                >
                                    <div className="mb-4 bg-gray-50 w-16 h-16 rounded-2xl flex items-center justify-center group-hover:bg-white group-hover:shadow-md transition-all">
                                        {item.icon}
                                    </div>
                                    <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">{item.title}</h3>
                                    <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* 3. Detailed Features - Bento Grid Style - UPDATED: Tighter Spacing, More Interactive */}
            <Container maxWidth="lg" sx={{ py: 16 }}>
                <div className="text-center mb-12">
                    <Typography variant="h2" className="text-3xl lg:text-4xl font-bold text-gray-900">
                        What You Can Do with Oye Creators
                    </Typography>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Feature 1: Centralized Network (Large) */}
                    <div className="md:col-span-2 bg-gray-900 rounded-3xl p-8 lg:p-10 text-white relative overflow-hidden group hover:shadow-2xl hover:shadow-blue-900/20 transition-all duration-500 cursor-pointer">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500 rounded-full mix-blend-overlay filter blur-3xl opacity-20 group-hover:opacity-40 transition-opacity duration-500"></div>
                        <div className="relative z-10">
                            <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                                <HubIcon className="text-white" />
                            </div>
                            <h3 className="text-2xl font-bold mb-4">Centralized Creator Network</h3>
                            <p className="text-gray-300 mb-6 max-w-md group-hover:text-white transition-colors">Manage your entire creator ecosystem in one place. Store profiles, track history, and organize by affinity.</p>
                            <ul className="space-y-2 text-sm text-gray-400">
                                <li className="flex items-center gap-2"><CheckCircleIcon fontSize="small" className="text-green-400" /> Creator profiles & audience insights</li>
                                <li className="flex items-center gap-2"><CheckCircleIcon fontSize="small" className="text-green-400" /> Track engagement history</li>
                            </ul>
                        </div>
                    </div>

                    {/* Feature 2: Discovery (Tall) */}
                    <div className="md:row-span-2 bg-purple-50 rounded-3xl p-8 border border-purple-100 relative overflow-hidden group hover:border-purple-300 transition-colors duration-300">
                        <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-6 text-purple-600 group-hover:scale-110 transition-transform">
                            <SearchIcon />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-3">Data-Driven Discovery</h3>
                        <p className="text-gray-600 text-sm mb-6">Find creators based on relevance, not just vanity metrics. Filter by niche, location, and audience quality.</p>
                        <div className="bg-white rounded-xl p-4 shadow-sm border border-purple-100 group-hover:shadow-md transition-shadow">
                            <div className="text-xs font-bold text-gray-400 uppercase mb-2">Filter By</div>
                            <div className="flex flex-wrap gap-2">
                                {['Location', 'Age', 'Engagement', 'Niche'].map(tag => (
                                    <span key={tag} className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-md font-medium hover:bg-purple-200 transition-colors cursor-default">{tag}</span>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Feature 3: Outreach */}
                    <div className="bg-white border border-gray-200 rounded-3xl p-8 hover:border-blue-400 hover:shadow-lg transition-all duration-300 group cursor-pointer">
                        <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mb-6 text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-colors">
                            <VerifiedUserIcon />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">Streamlined Outreach</h3>
                        <p className="text-gray-500 text-sm">Scale outreach with personalized templates and automated tracking.</p>
                    </div>

                    {/* Feature 4: Campaign Mgmt */}
                    <div className="bg-white border border-gray-200 rounded-3xl p-8 hover:border-pink-400 hover:shadow-lg transition-all duration-300 group cursor-pointer">
                        <div className="w-12 h-12 bg-pink-50 rounded-xl flex items-center justify-center mb-6 text-pink-600 group-hover:bg-pink-600 group-hover:text-white transition-colors">
                            <InsightsIcon />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">Campaign Control</h3>
                        <p className="text-gray-500 text-sm">Track deliverables, timelines, and approvals in real-time.</p>
                    </div>

                    {/* Feature 5: Performance (Wide) */}
                    <div className="md:col-span-2 bg-gradient-to-r from-gray-900 to-gray-800 rounded-3xl p-8 lg:p-10 text-white flex flex-col md:flex-row items-center gap-8 group hover:shadow-2xl transition-shadow cursor-default">
                        <div className="flex-1">
                            <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                                <TrendingUpIcon />
                            </div>
                            <h3 className="text-2xl font-bold mb-4">Content & Performance Tracking</h3>
                            <p className="text-gray-300">Measure what matters. Track content performance, views, and ROI to optimize future campaigns.</p>
                        </div>
                        <div className="w-full md:w-1/3 bg-white/5 rounded-xl p-4 border border-white/10 backdrop-blur-sm group-hover:bg-white/10 transition-colors">
                            <div className="flex justify-between text-xs text-gray-400 mb-2">
                                <span>Campaign ROI</span>
                                <span className="text-green-400 font-bold">↑ 24%</span>
                            </div>
                            <div className="h-24 flex items-end gap-1">
                                {[40, 65, 45, 80, 55, 90].map((h, i) => (
                                    <div key={i} className="flex-1 bg-blue-500/80 rounded-t-sm group-hover:bg-blue-400 transition-colors delay-[50ms]" style={{ height: `${h}%` }}></div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Feature 6: Payments */}
                    <div className="bg-green-50 rounded-3xl p-8 border border-green-100 hover:border-green-300 transition-colors group">
                        <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-6 text-green-600 group-hover:rotate-12 transition-transform">
                            <GavelIcon />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">Payments & Compliance</h3>
                        <p className="text-gray-600 text-sm">Automated payouts, contracts, and tax compliance built for India.</p>
                    </div>
                </div>
            </Container>

            {/* 4. Target Audience Slider (Simple Cards) - UPDATED: Tighter Spacing */}
            <Box className="bg-gray-50 py-16 px-6 border-y border-gray-100">
                <Container maxWidth="lg">
                    <div className="text-center mb-10">
                        <Typography variant="h2" className="text-3xl font-bold text-gray-900">Who Is Oye Creators For?</Typography>
                    </div>
                    <Grid container spacing={4}>
                        {[
                            { role: 'Brands', text: 'Launch scalable campaigns and build long-term relationships.' },
                            { role: 'Agencies', text: 'Manage multiple clients and standardize workflows.' },
                            { role: 'Startups', text: 'Access authentic nano-creators at affordable costs.' }
                        ].map((card, idx) => (
                            <Grid item xs={12} md={4} key={idx}>
                                <div className="bg-white p-8 rounded-2xl shadow-sm text-center border border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 cursor-pointer">
                                    <h3 className="text-xl font-bold text-gray-900 mb-4">{card.role}</h3>
                                    <p className="text-gray-600">{card.text}</p>
                                </div>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* 5. CTA Section - UPDATED: Tighter Spacing */}
            <Container maxWidth="md" sx={{ py: 16, textAlign: 'center' }}>
                <Typography variant="h2" className="text-4xl lg:text-5xl font-extrabold text-gray-900 mb-6">
                    Start Building Your <br /> <span className="text-primary-500">Creator Network</span>
                </Typography>
                <Typography className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto">
                    Join brands and agencies already using Oye Creators to run smarter, faster, and more reliable influencer campaigns.
                </Typography>
                <div className="flex flex-col sm:flex-row justify-center gap-4">
                    <Button
                        variant="contained"
                        size="large"
                        href="https://outlook.office365.com/book/ProductDemo@ttlmedia.in/?ismsaljsauthenabled=true"
                        target="_blank"
                        rel="noopener noreferrer"
                        endIcon={<ArrowForwardIcon />}
                        sx={{
                            borderRadius: '50px',
                            px: 6, py: 2,
                            fontSize: '1.1rem',
                            fontWeight: 'bold',
                            background: '#E91E63', // Primary brand color
                            boxShadow: '0 20px 40px -10px rgba(233, 30, 99, 0.4)',
                            transition: 'transform 0.2s',
                            '&:hover': { background: '#D81B60', transform: 'scale(1.05)' }
                        }}
                    >
                        Book a Demo
                    </Button>
                </div>
            </Container>
        </Box>
    );
};

export default DiscoverCreators;
